<template>
  <div v-if="visible" class="bg-white rounded-3 p-3 shadow-sm mb-3">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <span class="fw-semibold">
        <i class="fa-solid fa-star text-warning me-2" />{{ $t('Loyalty') }}
      </span>
      <span v-if="loading"><i class="fa-solid fa-spinner fa-spin" /></span>
    </div>

    <template v-if="!loading">
      <!-- Balances -->
      <div
        v-for="b in balances"
        :key="b.program_id"
        class="d-flex justify-content-between"
        style="font-size: 13px;"
      >
        <span class="text-muted">{{ b.program_name }}</span>
        <span class="fw-semibold">{{ formatPoints(b.balance) }} {{ $t('pts') }}</span>
      </div>
      <div v-if="!balances.length" class="text-muted" style="font-size: 13px;">
        {{ $t('No points yet') }}
      </div>

      <!-- Earn preview -->
      <div class="d-flex justify-content-between border-top mt-2 pt-2" style="font-size: 13px;">
        <span class="text-success">
          <i class="fa-solid fa-arrow-up me-1" />{{ $t('Points to earn') }}
        </span>
        <span class="fw-bold text-success">+{{ formatPoints(earnPoints) }} {{ $t('pts') }}</span>
      </div>

      <!-- Applied redemptions on this bill -->
      <div v-if="redemptions.length" class="border-top mt-2 pt-2">
        <div class="text-muted mb-1" style="font-size: 12px;">{{ $t('Redeemed Rewards') }}</div>
        <div
          v-for="r in redemptions"
          :key="r.reference"
          class="d-flex justify-content-between align-items-center py-1"
          style="font-size: 13px;"
        >
          <span>
            <i class="fa-solid fa-gift me-1 text-primary" />{{ $t(r.reward_name) }}
            <span class="text-muted">(-{{ formatPoints(r.point_cost) }} {{ $t('pts') }})</span>
          </span>
          <button
            v-if="!finalized"
            type="button"
            class="btn btn-sm btn-link text-danger p-0"
            :disabled="busy"
            @click="cancelRedeem(r)"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>

      <!-- Redeem action — แลกได้ทั้งบิลจริง (billId) และก่อนมีบิล (sessionToken → BE สร้างบิลให้) -->
      <button
        type="button"
        class="btn btn-sm btn-outline-primary w-100 mt-2"
        :disabled="busy || finalized"
        @click="showRewards = !showRewards"
      >
        <i class="fa-solid fa-gift me-1" />{{ $t('Redeem Reward') }}
      </button>

      <div v-if="showRewards" class="mt-2">
        <div
          v-for="rw in rewardsAffordable"
          :key="rw.id"
          class="d-flex align-items-center gap-2 border rounded-3 p-2 mb-2 bg-white shadow-sm"
        >
          <div
            class="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
            :class="rw.reward_type === 'free_product' ? 'bg-warning-subtle text-warning' : 'bg-info-subtle text-info'"
            style="width: 36px; height: 36px;"
          >
            <i :class="rw.reward_type === 'free_product' ? 'fa-solid fa-gift' : 'fa-solid fa-tag'" />
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold text-truncate" style="font-size: 13px;">{{ $t(rw.name) }}</div>
            <div class="text-muted d-flex align-items-center gap-2" style="font-size: 11px;">
              <span><i class="fa-solid fa-star text-warning me-1" />{{ formatPoints(rw.point_cost) }} {{ $t('pts') }}</span>
              <span v-if="rw.reward_type === 'discount'" class="badge bg-info-subtle text-info">
                {{ $storeIndex.formatThaiCurrency(rw.value) }}
              </span>
            </div>
          </div>
          <span v-if="rw.reward_in_stock === false" class="badge bg-secondary flex-shrink-0">
            {{ $t('Out of stock') }}
          </span>
          <button
            v-else
            type="button"
            class="btn btn-sm btn-theme text-white rounded-pill px-3 flex-shrink-0"
            :disabled="busy"
            @click="redeem(rw)"
          >
            <i class="fa-solid fa-arrow-right-arrow-left me-1" />{{ $t('Redeem') }}
          </button>
        </div>
        <div v-if="!rewardsAffordable.length" class="text-muted text-center py-3" style="font-size: 13px;">
          {{ $t('No rewards available') }}
        </div>
      </div>
    </template>

    <!-- เลือก variant/modifier ก่อนแลกของแถม (free_product) -->
    <reward-select-modal
      v-if="selectedReward"
      ref="rewardModal"
      :reward-product-id="selectedReward.reward_product_id"
      :reward-name="selectedReward.name"
      :loading="redeeming"
      @confirm="onRewardConfirm"
      @close="selectedReward = null"
    />
  </div>
</template>

<script lang="js">
import { Loyalty } from '@/services/endpoint/loyalty.service';
import { Sessions } from '@/services/endpoint/pos.service';
import RewardSelectModal from './RewardSelectModal.vue';

export default {
  name: 'LoyaltyBillPanel',
  components: { RewardSelectModal },
  props: {
    billId: { type: [Number, String], default: 0 },
    // The bill must not be paid/voided/refunded before redeem is allowed.
    finalized: { type: Boolean, default: false },
    // เมื่อ parent ฝัง loyalty มากับ bills response แล้ว (check-bill เส้นเดียว) ใช้ตัวนี้แทนการ fetch
    loyalty: { type: Object, default: null },
    // โหมด session (ยังไม่มีบิล) — แลกผ่านเส้น session-redeem (BE สร้างบิลให้) เมื่อไม่มี billId
    sessionToken: { type: String, default: '' }
  },
  emits: ['changed'],
  data() {
    return {
      visible: false,
      loading: false,
      busy: false,
      showRewards: false,
      balances: [],
      rewardsAffordable: [],
      redemptions: [],
      earnPoints: 0,
      // free_product ที่ต้องเลือก variant/modifier ก่อนแลก
      selectedReward: null,
      redeeming: false
    };
  },
  watch: {
    billId() { if (!this.loyalty) this.fetch(); },
    loyalty: {
      handler(v) { if (v) this.applyFromProp(v); },
      deep: true
    }
  },
  mounted() {
    if (this.loyalty) this.applyFromProp(this.loyalty);
    else this.fetch();
  },
  methods: {
    formatPoints(value) {
      return (parseInt(value) || 0).toLocaleString();
    },
    // Server is the source of truth; FE only renders. Normalize defensively.
    applyData(d) {
      const data = d || {};
      this.balances = data.balances || [];
      this.rewardsAffordable = data.rewards_affordable || [];
      this.redemptions = data.redemptions || data.redeemed || [];
      const ep = data.earn_preview;
      this.earnPoints = (ep && typeof ep === 'object') ? (ep.points || 0) : (ep || 0);
    },
    // ใช้ loyalty ที่ฝังมากับ bills response (ไม่ยิง API เพิ่ม)
    applyFromProp(data) {
      if (!data || data.enabled === false || data.has_customer === false) {
        this.visible = false;
        return;
      }
      this.applyData(data);
      this.visible = true;
    },
    // หลัง redeem/cancel: ถ้า parent เป็นเจ้าของข้อมูล (prop) ให้มันรีเฟรช bills เอง; ไม่งั้น fetch
    async refresh() {
      if (this.loyalty) {
        this.$emit('changed');
      } else {
        await this.fetch();
        this.$emit('changed');
      }
    },
    async fetch() {
      const id = this.billId;
      if (!id || String(id).startsWith('local-')) {
        this.visible = false;
        return;
      }
      this.loading = true;
      try {
        const res = await Loyalty.billLoyalty(id);
        const data = res.data?.data ?? res.data ?? {};
        // API returns `enabled` (system on/off) + `has_customer` — hide the whole
        // panel when loyalty is off or the bill has no customer attached.
        if (data.enabled === false || data.has_customer === false) {
          this.visible = false;
          return;
        }
        this.applyData(data);
        this.visible = true;
      } catch (err) {
        // No permission / loyalty off → hide the panel.
        this.visible = false;
      } finally {
        this.loading = false;
      }
    },
    redeem(reward) {
      // free_product ที่ต้องเลือก (config/variant หรือมี modifier) → เปิด modal เหมือนการสั่งจริง
      // ถ้าเป็น simple ไม่มีตัวเลือก → แลกได้เลย ไม่เด้ง modal
      if (reward.reward_type === 'free_product' && reward.reward_product_id && reward.needs_selection) {
        this.selectedReward = reward;
        return;
      }
      this.doRedeem(reward, {});
    },
    onRewardConfirm(selection) {
      if (this.selectedReward) this.doRedeem(this.selectedReward, selection);
    },
    async doRedeem(reward, selection) {
      this.busy = true;
      this.redeeming = true;
      try {
        const payload = { reward_id: reward.id };
        if (selection.product_id) payload.product_id = selection.product_id;
        if (selection.modifier_option_ids && selection.modifier_option_ids.length) {
          payload.modifier_option_ids = selection.modifier_option_ids;
        }
        // มีบิลจริง → แลกบนบิลนั้น; ยังไม่มีบิล (session mode) → BE สร้างบิลให้แล้วแลก
        if (this.billId) {
          await Loyalty.redeem(this.billId, payload);
        } else if (this.sessionToken) {
          await Sessions.loyaltyRedeem(this.sessionToken, payload);
        } else {
          return;
        }
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Reward redeemed.'));
        this.showRewards = false;
        // ปิด modal ให้ถูกวิธี (ถ้าเปิดอยู่) → @close จะเคลียร์ selectedReward
        if (this.$refs.rewardModal) this.$refs.rewardModal.close();
        else this.selectedReward = null;
        // Discount/free-product rewards change the bill total — let the parent resync the bill.
        await this.refresh();
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Failed to redeem reward'));
      } finally {
        this.busy = false;
        this.redeeming = false;
      }
    },
    async cancelRedeem(redemption) {
      const reference = typeof redemption === 'object' ? redemption.reference : redemption;
      let isRestock = true;

      // ของแถม = สินค้าจริงที่หักสต๊อก → ถามว่าคืนวัตถุดิบไหม (เหมือน cancel item ปกติ)
      // ถ้าครัวทำไปแล้วก็ไม่ควรคืน — แต้มคืนเสมอไม่เกี่ยวกับ restock
      if (redemption && redemption.reward_type === 'free_product') {
        const result = await this.$swal({
          title: this.$t('_loyalty.Cancel reward?'),
          text: this.$t('_loyalty.Return ingredients to stock?'),
          icon: 'question',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: this.$t('Restock'),
          denyButtonText: this.$t("Don't restock"),
          cancelButtonText: this.$t('Close'),
          confirmButtonColor: '#198754',
          denyButtonColor: '#6c757d',
        });
        if (result.isDismissed) return; // กดปิด/นอกกล่อง = ไม่ทำอะไร
        isRestock = result.isConfirmed; // confirm = คืนสต๊อก, deny = ไม่คืน
      }

      this.busy = true;
      try {
        await Loyalty.cancelRedeem(this.billId, reference, isRestock);
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Redemption cancelled.'));
        await this.refresh();
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Failed to cancel redemption'));
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
